/**
 * <SpellCheckDialog> — confirmation modal shown after a content save is
 * intercepted by the UK-English validator.
 *
 * Lists every US→UK suggestion with surrounding context. The admin can:
 *   - "Apply all UK fixes" → POST /api/admin/spell-check/apply, save the cleaned payload
 *   - "Save as-is" → bypass the validator and save the original (intentional US words)
 *   - "Cancel" → close the dialog and edit by hand
 */
import React from 'react';
import { AlertTriangle } from 'lucide-react';

import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';

const SpellCheckDialog = ({ open, onOpenChange, findings, onApplyAll, onSaveAsIs, busy }) => {
  const totalIssues = findings.reduce((acc, f) => acc + (f.issues?.length || 0), 0);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-2xl" data-testid="spell-check-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            <DialogTitle className="text-2xl font-bold text-[#020617]">
              UK English check
            </DialogTitle>
          </div>
          <DialogDescription className="text-[#64748B] pt-1">
            Found <strong>{totalIssues}</strong> US spelling{totalIssues === 1 ? '' : 's'} across{' '}
            <strong>{findings.length}</strong> field{findings.length === 1 ? '' : 's'}. Review the suggested UK swaps below.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[400px] overflow-y-auto -mx-2 px-2 space-y-3" data-testid="spell-check-findings">
          {findings.map((f, fi) => (
            <div key={fi} className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-3" data-testid={`spell-check-field-${fi}`}>
              <div className="text-[10px] uppercase tracking-wider font-bold text-[#64748B] mb-1">{f.path}</div>
              <ul className="space-y-1">
                {f.issues.map((i, ii) => (
                  <li key={ii} className="text-sm text-[#020617] flex flex-wrap items-baseline gap-2" data-testid={`spell-check-issue-${fi}-${ii}`}>
                    <span className="line-through text-[#DC2626] font-mono">{i.us}</span>
                    <span className="text-[#94A3B8]">→</span>
                    <span className="text-[#047857] font-mono font-semibold">{i.uk}</span>
                    <span className="text-xs text-[#64748B]">…{i.context}…</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="flex gap-2 pt-2 flex-wrap">
          <Button
            type="button"
            onClick={onApplyAll}
            disabled={busy}
            className="flex-1 bg-[#0052FF] text-white hover:bg-[#003ECC]"
            data-testid="spell-check-apply-all"
          >
            {busy ? 'Applying…' : 'Apply all UK fixes & save'}
          </Button>
          <Button
            type="button"
            onClick={onSaveAsIs}
            disabled={busy}
            className="flex-1 bg-[#F1F5F9] text-[#020617] hover:bg-[#E2E8F0]"
            data-testid="spell-check-save-as-is"
          >
            Save as-is
          </Button>
          <Button
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            className="bg-white border border-[#E2E8F0] text-[#020617] hover:bg-[#F8FAFC]"
            data-testid="spell-check-cancel"
          >
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SpellCheckDialog;
